import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { preloaderContent } from '../../../constants/copyright'
import Icon from '../../baseComponents/gui/icon/Icon'
import useStateControls from '../../../hooks/useStateControls'
import { useLoadElfController } from '../../../utils/scene/react/hooks/useLoadElfController'
import { setElfImportPromise } from '../../../controllers/elf/items/import'
import { baseThreeImports } from '../../../utils/scene/utils/import/import-three'
import useCustomBridge from '../../../hooks/useCustomBridge'
import { useRequestsHandlerData } from '../../../hooks/useRequestsHandlerData'
import { usePreloadPromises } from '../../../hooks/usePreloadPromises'
import { useRequests } from '../../../redux/reducer/requests'
import CustomBridge from '../../../controllers/bridge/CustomBridge'
import { isStub } from '../../../utils/stub'
import { useUser } from '../../../redux/reducer/user'
import { useModal } from '../../../hooks/useModal'
import { getQueryParams } from '../../../utils/findGetParameter'
import { useApplicationCallbacks } from '../../../providers/application-callbacks'
import { START } from '../../../constants/shared/states/names'
import { defaultStore, defaultContur, conturs, useTranslation } from '../../../translations'
import { getPlaidLanguageId } from '../../../translations/utils'
import { productsApi } from '../../../utils/api/productsApi'
import { getIsDebug } from '../../../utils/scene/debug/getIsDebug'
import { useLanguageControls } from '../../../hooks/useLanguageControls'
import { useFeatureToggle } from '../../../hooks/useFeatureToggle'
import { safeHTML } from '../../../utils/safeHTML'
import useServiceData from '../../../hooks/useServiceData'

const { logo } = preloaderContent

const Preloader = React.forwardRef(function Preloader(_, ref) {
  const { profile } = useUser()
  const { authData } = useRequests()
  const callbacks = useApplicationCallbacks()
  const { setState } = useStateControls()
  const { addModal } = useModal()
  const { language } = useLanguageControls()
  const { t } = useTranslation()
  const { get } = useServiceData()

  const [progress, setProgress] = useState(0)
  const [elfWrapper, setElfWrapper] = useState(null)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const isElfInitializingRef = useRef(false)
  const elfWrapperRef = useRef(null)
  const initializeElfRef = useRef(null)

  const isMeContur = defaultContur === conturs.me

  const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')

  const loadProportion = useMemo(() => {
    if (isMeContur) {
      return { promises: 100, controller: 0 }
    }
    return { promises: 80, controller: 20 }
  }, [isMeContur])

  // Запрашиваем базовый данные для наполнения контентом
  const preloadPromises = usePreloadPromises({
    setProgress,
    loadProportion,
    fulfilled: () => {
      setProgress(100)
    },
  })

  // Общая логика инициализации эльфа после загрузки
  const initializeElf = useCallback(
    async (wrapper, shouldUpdateProgress = false, skipGetUser = false) => {
      if (isElfInitializingRef.current) {
        return
      }
      isElfInitializingRef.current = true

      try {
        await wrapper.controller.loadManifestSelect()
        await wrapper.controller.loadingSelect()
        await wrapper.controller.create()

        // Запускаем update loop после создания эльфа, если он еще не запущен
        if (wrapper.controller && wrapper.controller.elf) {
          wrapper.controller.paused = false
          wrapper.controller.elf.paused = false

          if (!wrapper.controller.frameId) {
            wrapper.controller.update() // Запускаем update напрямую - он сам запустит requestAnimationFrame
          }

          // Для ME добавляем приветственную анимацию после загрузки эльфа
          if (isMeContur) {
            callbacks.addAnimations([
              {
                state: 'any',
                animation: 'hello',
              },
            ])
          }
        }

        if (!skipGetUser) {
          if (window.self === window.top) {
            const params = getQueryParams()
            if (params?.phone) {
              callbacks.getAccessToken(params)
              getIsDebug() &&
                productsApi.addHeaders({
                  'plaid-platform': 'web',
                  'plaid-version': '1.0.0',
                  'plaid-store-id': defaultStore,
                  'plaid-language-id': getPlaidLanguageId(language),
                })
            }
          } else {
            setTimeout(() => CustomBridge.instance.send({ name: 'GET_USER', type: 0 }), 1000)
          }
        }

        if (shouldUpdateProgress) {
          setProgress((prev) => prev + loadProportion.controller)
        }
      } catch (error) {
        // Не пробрасываем ошибку дальше, чтобы не упало приложение
      } finally {
        isElfInitializingRef.current = false
      }
    },
    [callbacks, loadProportion, setProgress, isMeContur],
  )

  const isStubValue = isStub(profile?.uid)

  useCustomBridge()

  useRequestsHandlerData([
    {
      address: 'requestsUserGetAccessToken',
      fulfilled: callbacks.auth,
    },
    {
      address: 'requestsUserAuth',
      fulfilled(payload) {
        if (payload?.isGameStopped) {
          addModal({
            type: 'inactiveAppModal',
          })
        } else {
          setIsAuthorized(true)
          CustomBridge.instance.send({ name: 'CHECK_USER_DATA_FILLED', type: 0 })

          if (gameTamagotchiBeauties) {
            callbacks.getBeautiesBalance()
          }

          if (authData?.share) setState('share')
          else {
            preloadPromises()
          }
        }
      },
    },
  ])

  // Для прямого входа через phone параметр: запускаем только авторизацию (без загрузки эльфа)
  useEffect(() => {
    if (isMeContur && window.self === window.top) {
      const params = getQueryParams()

      if (params?.phone && !isAuthorized) {
        callbacks.getAccessToken(params)
      }
    }
  }, [isMeContur, isAuthorized, callbacks])

  useLoadElfController({
    getLibsPromise: baseThreeImports,
    getWrapperPromise: () => import('../../../controllers/elf/ElfWrapper'),
    beforeInit: ({ promise }) => {
      if (!isMeContur) {
        setElfImportPromise(promise)
      }
    },
    async afterInit({ wrapper }) {
      if (isMeContur) {
        // Для me контура: резолвим промис сразу, загрузка ресурсов будет параллельно с промисами
        setElfImportPromise(Promise.resolve({ wrapper }))
        setElfWrapper(wrapper) // Сохраняем wrapper для запуска загрузки параллельно с промисами

        if (window.self !== window.top) {
          setTimeout(() => CustomBridge.instance.send({ name: 'GET_USER', type: 0 }), 1000)
        }

        return
      } else {
        // Для cis контура - всегда старая логика (блокирующая загрузка)
        await initializeElf(wrapper, true)
      }
    },
  })

  // Сохраняем ссылки в ref для использования в setTimeout
  useEffect(() => {
    elfWrapperRef.current = elfWrapper
  }, [elfWrapper])

  useEffect(() => {
    initializeElfRef.current = initializeElf
  }, [initializeElf])

  // Для me: Загрузка выполняется в фоне и не блокирует никакие запросы
  useEffect(() => {
    if (!isMeContur || !elfWrapper) return

    let timeoutId
    let asyncCallback

    const scheduleLoad = () => {
      // Проверяем еще раз перед планированием загрузки
      if (isElfInitializingRef.current || !elfWrapperRef.current || !initializeElfRef.current) {
        return
      }

      // Запускаем загрузку эльфа полностью асинхронно, используя несколько уровней отложенного выполнения
      // чтобы гарантировать, что это не блокирует основной поток и запросы
      timeoutId = setTimeout(() => {
        const scheduleAsync = (callback) => {
          if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            asyncCallback = window.requestIdleCallback(callback, { timeout: 100 })
          } else {
            asyncCallback = setTimeout(callback, 0)
          }
        }

        scheduleAsync(async () => {
          try {
            await initializeElfRef.current(elfWrapperRef.current, false, true)
          } catch (error) {
            // Ошибка уже обработана в initializeElf
          }
        })
      }, 0)
    }

    scheduleLoad()

    // Cleanup функция для отмены запланированных операций
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (asyncCallback) {
        if (typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(asyncCallback)
        } else {
          clearTimeout(asyncCallback)
        }
      }
    }
  }, [isMeContur, elfWrapper])

  useEffect(() => {
    if (Math.round(progress) === 100 && !isStubValue && profile) {
      setState(START)
    }
  }, [progress, isStubValue, profile, get])

  useEffect(() => {
    if (isStubValue && isStubValue !== 'exeption') setState('stub')
  }, [isStubValue])

  return (
    <section
      className={'preloader'}
      ref={ref}
    >
      <div className={'preloader__content'}>
        <div className={'preloader__body'}>
          <div className={'preloader__logo'}>
            <Icon name={logo.icon} />
          </div>

          <h1
            className={'preloader__title'}
            suppressHydrationWarning
          >
            {safeHTML(t('preloader.title'))}
          </h1>

          <div className={'preloader__statusbar'}>
            <div className={'preloader__statusbar-block'}>
              <div
                className={'preloader__statusbar-progress'}
                style={{ transform: `scaleX(${progress / 100})` }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
})
export default Preloader
Preloader.propTypes = {}
